import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { 
  AlertTriangle, 
  Clock, 
  ChevronRight,
  CheckCircle2,
  Loader2
} from "lucide-react";
import { cn } from "@/lib/utils";
import { useActionsData } from "@/hooks/useActionsData";

interface ActionsBadgeProps {
  className?: string;
}

export function ActionsBadge({ className }: ActionsBadgeProps) {
  const navigate = useNavigate();
  const { items, stats, loading } = useActionsData();

  // Count expirations (documents with expiresAt)
  const expirationCount = items.filter(item => item.expiresAt).length;
  const total = stats.total;
  const urgentCount = stats.urgent;
  const hasUrgent = urgentCount > 0 || stats.expired > 0;

  if (loading) {
    return (
      <Card className={cn("overflow-hidden", className)}>
        <CardContent className="p-3 flex items-center justify-center">
          <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  if (total === 0) {
    return (
      <Card className={cn("overflow-hidden bg-success/5 border-success/20", className)}>
        <CardContent className="p-3">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-success/10 flex items-center justify-center">
              <CheckCircle2 className="w-5 h-5 text-success" />
            </div>
            <div className="flex-1">
              <p className="text-sm font-medium text-foreground">Tout est en ordre</p>
              <p className="text-xs text-muted-foreground">Aucune action en attente</p>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      className={className}
    >
      <Card 
        className={cn(
          "overflow-hidden cursor-pointer transition-all hover:shadow-md active:scale-[0.98]",
          hasUrgent 
            ? "bg-warning/5 border-warning/30 hover:border-warning/50" 
            : "hover:border-primary/30"
        )}
        onClick={() => navigate("/actions")}
      >
        <CardContent className="p-3">
          <div className="flex items-center gap-3">
            <div className={cn(
              "relative w-9 h-9 rounded-lg flex items-center justify-center",
              hasUrgent ? "bg-warning/10" : "bg-primary/10"
            )}>
              {hasUrgent ? (
                <motion.div
                  animate={{ scale: [1, 1.1, 1] }}
                  transition={{ duration: 2, repeat: Infinity }}
                >
                  <AlertTriangle className="w-5 h-5 text-warning" />
                </motion.div>
              ) : (
                <Clock className="w-5 h-5 text-primary" />
              )}
              {/* Pulse indicator for urgent */}
              {hasUrgent && (
                <motion.div 
                  className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 bg-destructive rounded-full"
                  animate={{ scale: [1, 1.3, 1], opacity: [1, 0.7, 1] }}
                  transition={{ duration: 1.5, repeat: Infinity }}
                />
              )}
            </div>
            
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <p className="text-sm font-medium text-foreground">
                  {total} action{total > 1 ? 's' : ''} en attente
                </p>
                <Badge 
                  variant={hasUrgent ? "destructive" : "secondary"} 
                  className="text-[10px] px-1.5 py-0"
                >
                  {hasUrgent ? `${urgentCount} urgent${urgentCount > 1 ? 'es' : 'e'}` : 'À faire'}
                </Badge>
              </div>
              <p className="text-xs text-muted-foreground">
                {expirationCount > 0 && `${expirationCount} expiration${expirationCount > 1 ? 's' : ''}`}
              </p>
            </div>

            <ChevronRight className="w-4 h-4 text-muted-foreground" />
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
}
